import React from "react";
import { InputText, Label, Dropdown } from "../../../../components/Inputs";
import DelphiConnectionListForm from "./DelphiConnectionListForm";
import {
  STATUS_LABELS,
  statusIsEditable,
  STATUS_IDS,
  ACTION_IDS,
} from "../../../../components/Utils";
import { PrimaryButton, SecondaryButton } from "../../../../components/Buttons";
import ConfirmationDialog from "../../../../components/ConfirmationDialog";
import {
  PermissionResource,
  PermissionSection,
  Resources,
} from "../../../Permissions";
import ADConnectionListForm from "./ADConnectionListForm";
import ANPConnectionListForm from "./ANPConnectionListForm";
import JarvisConnectionListForm from "./JarvisConnectionListForm";
import NGUMStatusObserver from "../../../NGum/containers/NGUMStatusObserver";

const YesNoOptions = [
  { label: "Ja", value: "Ja" },
  { label: "Nein", value: "Nein" },
];

const ITConnectionForm = React.forwardRef(function ITConnectionForm(
  {
    className = "",
    model = {
      ptConnection: "",
      ptUsername: "",
      ptPassword: "",
      adConnection: "",
      adUsername: "",
      adPassword: "",
      adCellNumber: "",
      anpConnection: {},
      delphiConnections: [],
      jarvisConnection: null,
    },
    kdlCompanyList = [],
    userCompany,
    canReadAdminData,
    rows = 5,
    vfEmailPermission,
    ngumUserIdPermission,
    getCompanyData,
    getEmployeeData,
    roleList,
    userDisabled,
    isDL = false,
    children,
  },
  ref,
) {
  const [state, setState] = React.useState(() => {
    const defaultStatusId = STATUS_IDS.NEW;
    const adStatus_id =
      model.adStatus_id && model.adStatus_id !== ""
        ? model.adStatus_id
        : defaultStatusId;
    const ptStatus_id =
      model.ptStatus_id && model.ptStatus_id !== ""
        ? model.ptStatus_id
        : defaultStatusId;
    return {
      adConnection: model.adConnection || "",
      adStatus_id,
      ptConnection: model.ptConnection || "",
      ptUsername: model.ptUsername || "",
      ptPassword: model.ptPassword || "",
      ptAction_id: model.ptAction_id || "",
      ptStatus_id,
      ptStatus: model.ptStatus || STATUS_LABELS[ptStatus_id],
      error: {
        ptConnection: null,
      },
    };
  });

  const adConnectionRef = React.useRef(null);
  const anpConnectionRef = React.useRef(null);
  const connectionsRef = React.useRef(null);
  const jarvisConnectionRef = React.useRef(null);
  const confirmRef = React.useRef(null);

  const validate = () => {
    let modelData = {};
    // vodafone account
    const adModel = adConnectionRef.current.getModel();
    if (!adModel) {
      return null;
    }
    modelData = { ...modelData, ...adModel };
    // anp account
    modelData.anpConnection = !anpConnectionRef.current
      ? {}
      : anpConnectionRef.current.getModel();
    // pt connection
    const { error, adConnection, adStatus_id, ...ptModel } = state;
    modelData = { ...modelData, ...ptModel };
    // delphi connections
    const connections = !connectionsRef.current
      ? []
      : connectionsRef.current.getModel();
    modelData.delphiConnections = connections;
    // jarvis connection
    modelData.jarvisConnection = !jarvisConnectionRef.current
      ? null
      : jarvisConnectionRef.current.getModel();

    const pModel = model || {};
    const { id, ad_id, pt_id } = pModel;
    modelData.id = id;
    modelData.ad_id = ad_id;
    modelData.pt_id = pt_id;
    return modelData;
  };

  React.useImperativeHandle(ref, () => ({
    getModel: () => validate(),
  }));

  const executeChange = (connectionType, actionId, statusId) => {
    const prefix = connectionType.toLowerCase();
    setState((prev) => ({
      ...prev,
      [`${prefix}Action_id`]: actionId,
      [`${prefix}Status_id`]: statusId,
      [`${prefix}Status`]: STATUS_LABELS[statusId],
    }));
  };
  const onChange = (connectionType) => {
    const title = `${connectionType}-Zugang ändern`;
    const text = `Wollen Sie eine Verlängerung des ${connectionType}-Zugangs wirklich beantragen?`;
    const execFunc = () =>
      executeChange(connectionType, ACTION_IDS.UPDATE, STATUS_IDS.UPDATING);
    confirmRef.current.show(true, { title, text }, execFunc);
  };
  const onDelete = (connectionType) => {
    const title = `${connectionType}-Zugang löschen`;
    const text = `Wollen Sie eine Löschung des ${connectionType}-Zugangs wirklich beantragen?`;
    const execFunc = () =>
      executeChange(connectionType, ACTION_IDS.DELETE, STATUS_IDS.DELETING);
    confirmRef.current.show(true, { title, text }, execFunc);
  };
  const onADConnectionChange = (adConnection, adStatus_id) => {
    setState((prev) => ({ ...prev, adConnection, adStatus_id }));
  };
  const updateState = (patch) => setState((prev) => ({ ...prev, ...patch }));
  const renderConnectionBtns = (connectionType, model) => {
    const idAttr = `${connectionType.toLowerCase()}_id`;
    const statusAttr = `${connectionType.toLowerCase()}Status_id`;
    return (
      <div>
        {connectionType !== "AD" && (
          <PrimaryButton
            size="small"
            disabled={!statusIsEditable(model[statusAttr], model[idAttr])}
            onClick={() => onChange(connectionType)}
          >
            {`Ändern`}
          </PrimaryButton>
        )}
        <SecondaryButton
          className="margin-top"
          size="small"
          disabled={!statusIsEditable(model[statusAttr], model[idAttr])}
          onClick={() => onDelete(connectionType)}
        >
          {`Löschen`}
        </SecondaryButton>
      </div>
    );
  };

  const { ptConnection, ptUsername, ptPassword, ptStatus, ptStatus_id } = state;
  const adConnection = model.adConnection || state.adConnection || "";
  // flag to check the status of the underlying vodafone account. It ensures that:
  // 1. vodafone account exists
  // 2. vodafone account status is NEU, IN BEARBEITUNG oder ABGESCHLOSSEN
  const isVodafoneAccountPresent =
    adConnection.toLowerCase() === "ja" &&
    (state.adStatus_id === STATUS_IDS.NEW ||
      state.adStatus_id === STATUS_IDS.PROCESSING ||
      state.adStatus_id === STATUS_IDS.FINISHED);
  return (
    <div className={className}>
      <PermissionResource
        resource={Resources.Organisation.WorkerWizard.ADConnections}
      >
        <PermissionSection
          height="auto"
          id="vodafone-account"
          className="neg-margin-big-lr pad-big-lr pad-big-btm"
          isUnterWizardTitle
          idOffset={-164}
        >
          <ADConnectionListForm
            ref={adConnectionRef}
            className="pad-big-top"
            model={model}
            vfEmailPermission={vfEmailPermission}
            ngumUserIdPermission={ngumUserIdPermission}
            onChange={onADConnectionChange}
            readOnly={userDisabled}
          />
        </PermissionSection>
      </PermissionResource>
      <PermissionResource
        resource={Resources.Organisation.WorkerWizard.ANPAccount}
      >
        <PermissionSection
          height="auto"
          id="anp-zugang"
          className="w3-border-top neg-margin-big-lr pad-big-lr pad-big-btm"
          isUnterWizardTitle
          idOffset={-164}
        >
          <NGUMStatusObserver>
            <ANPConnectionListForm
              ref={anpConnectionRef}
              className="pad-big-top"
              model={model.anpConnection}
              getCompanyData={getCompanyData}
              roleList={roleList}
              isVodafoneAccountPresent={isVodafoneAccountPresent}
              readOnly={!isVodafoneAccountPresent || userDisabled}
            />
          </NGUMStatusObserver>
        </PermissionSection>
      </PermissionResource>
      <PermissionResource
        resource={Resources.Organisation.WorkerWizard.PTConnections}
      >
        <PermissionSection
          height="auto"
          id="iprov-zugang"
          className="w3-border-top neg-margin-big-lr pad-big-lr pad-big-btm"
          isUnterWizardTitle
          idOffset={-164}
        >
          <PTConnectionForm
            model={model}
            adConnection={adConnection}
            ptConnection={ptConnection}
            ptUsername={ptUsername}
            ptPassword={ptPassword}
            ptStatus={ptStatus}
            ptStatus_id={ptStatus_id}
            setState={updateState}
            renderConnectionBtns={renderConnectionBtns}
            children={children}
            disabled={
              !isVodafoneAccountPresent ||
              !statusIsEditable(ptStatus_id, model.pt_id, true) ||
              userDisabled
            }
          />
        </PermissionSection>
      </PermissionResource>
      <PermissionResource
        resource={Resources.Organisation.WorkerWizard.DelphiConnections}
      >
        <PermissionSection
          height="auto"
          id="delphi-zugaenge"
          className="w3-border-top neg-margin-big-lr pad-big-lr pad-big-btm"
          isUnterWizardTitle
          idOffset={-164}
        >
          <DelphiConnectionListForm
            ref={connectionsRef}
            className="pad-big-top"
            model={model.delphiConnections}
            kdlCompanyList={kdlCompanyList}
            userCompany={userCompany}
            disableDelphi={!isVodafoneAccountPresent || userDisabled}
            canReadAdminData={canReadAdminData}
            rows={rows}
          />
        </PermissionSection>
      </PermissionResource>
      <PermissionResource
        resource={Resources.Organisation.WorkerWizard.JarvisConnection}
      >
        <PermissionSection
          height="auto"
          id="jarvis-zugang"
          className="w3-border-top neg-margin-big-lr pad-big-lr pad-big-btm"
          isUnterWizardTitle
          idOffset={-162}
        >
          <JarvisConnectionListForm
            ref={jarvisConnectionRef}
            className="pad-big-top"
            model={model.jarvisConnection}
            kdlCompanyList={kdlCompanyList}
            getCompanyData={getCompanyData}
            getEmployeeData={getEmployeeData}
            readOnly={!isVodafoneAccountPresent || userDisabled} // disable until vodafone account is completed
            canReadAdminData={canReadAdminData}
            isDL={isDL}
          />
        </PermissionSection>
      </PermissionResource>
      <ConfirmationDialog ref={confirmRef} />
    </div>
  );
});

export default ITConnectionForm;

const PTConnectionForm = ({
  model,
  adConnection,
  ptConnection,
  ptUsername,
  ptPassword,
  ptStatus,
  ptStatus_id,
  setState,
  renderConnectionBtns,
  readOnly,
  disabled: _disabled,
}) => {
  const disabled =
    _disabled || !statusIsEditable(ptStatus_id, model.pt_id, true);
  return (
    <>
      {/* <div className="w3-border-top neg-margin-big-lr margin-big-top margin-btm" /> */}
      <div className="pad-big-top w3-cell-row">
        <div className="w3-cell w3-cell-top">
          <div className="w3-row neg-margin-lr">
            <div className="w3-col s2 pad-lr pad-top">
              <Label htmlFor="ptConnection">{`Prov.-tool-Zugang*`}</Label>
              <Dropdown
                id="ptConnection"
                className="w3-block w3-border"
                value={ptConnection}
                placeholder="Bitte auswählen"
                disabled={
                  disabled ||
                  adConnection !== "Ja" ||
                  ptStatus_id !== STATUS_IDS.NEW
                }
                options={YesNoOptions}
                onChange={(event) => setState({ ptConnection: event.value })}
                showClear
              />
            </div>
            <div className="w3-col s8 pad-lr">
              <div className="w3-row neg-margin-lr">
                <div className="w3-col s4 pad-lr pad-top">
                  <Label htmlFor="ptUsername">{`Prov.-tool-Kennung`}</Label>
                  <InputText
                    id="ptUsername"
                    className="w3-block w3-border pad round-input"
                    value={ptUsername}
                    readonly
                  />
                </div>
                <PermissionResource
                  resource={
                    Resources.Organisation.WorkerWizard.ConnectionPasswords
                  }
                >
                  <div className="w3-col s4 pad-lr pad-top">
                    <Label htmlFor="ptPassword">{`Prov.-tool-Passwort`}</Label>
                    <InputText
                      id="ptPassword"
                      className="w3-block w3-border pad round-input"
                      value={ptPassword}
                      readonly
                    />
                  </div>
                </PermissionResource>
              </div>
            </div>
            <div className="w3-col s2 pad-lr pad-top">
              <Label htmlFor="ptStatus">{`PT-Zugangstatus`}</Label>
              <InputText
                id="ptStatus"
                className="w3-block w3-border pad round-input"
                value={ptStatus}
                readonly
              />
            </div>
          </div>
        </div>
        {!_disabled && !readOnly && (
          <div
            className="w3-cell w3-cell-bottom alg-rht"
            style={{ width: "150px" }}
          >
            {renderConnectionBtns("PT", { ...model, ptStatus_id })}
          </div>
        )}
      </div>
    </>
  );
};
